// Setup › Seats (`/c/{slug}/setup/seats`, design spec §9; coverage rows 17–23, 26): the
// company's seats as a table, "New seat", and a detail drawer per seat (`?seat=`) to edit it
// (`PATCH` with `If-Match`), assign or unassign a person or a DE, set its metrics and weights
// (`kpi`) and close it (AlertDialog). Setup is managers only (the mode's roles); every write
// is a manager route. On load: seats, roles and the DE list (names of the DE holders).

import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  SearchField,
  Segmented,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArmchairIcon, ArrowUpRightIcon, PencilIcon, PlusIcon } from "lucide-react";
import { type ComponentProps, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { Seat } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { setupCrumb } from "../chart/chart-parts";
import type { CompanyAccess } from "../company";
import { SendSeatSetupDialog } from "../flows/flow-entry";
import { FlowModal } from "../flows/flow-panel";
import {
  type HolderKind,
  hireDeWithOnboarding,
  SeatHolderChoice,
  useDeChoices,
} from "../flows/seat-holder";
import { digitalEmployeesQuery, keys, rolesQuery, seatsQuery } from "../queries";
import { Loading } from "../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { useEveryPage } from "../use-every-page";
import { useSession } from "../use-feature";
import { SEAT_STATUS_LABELS } from "./labels";
import { ErrorNotice, FormError, MoreRows } from "./parts";
import { SeatDrawer } from "./seat-drawer";

type Filter = "active" | "closed" | "all";

export const SEAT_STATUS_TONE: Record<
  Seat["status"],
  NonNullable<ComponentProps<typeof Badge>["variant"]>
> = {
  proposed: "outline",
  open: "info",
  filled: "success",
  paused: "warning",
  closed: "neutral",
};

export function SeatsScreen({
  api,
  company,
  seatId,
  onSeat,
  create = false,
}: {
  api: Api;
  company: CompanyAccess;
  /** The seat open in the drawer (`?seat=`), if any. */
  seatId: string | undefined;
  onSeat: (id: string | undefined) => void;
  /** SEARCH-NOTIF: ⌘K's "New seat" opens the new-seat dialog at once (`?new=1`). */
  create?: boolean;
}) {
  const seats = useInfiniteQuery(seatsQuery(api, company.id));
  const roles = useInfiniteQuery(rolesQuery(api, company.id));
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  const [filter, setFilter] = useState<Filter>("active");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(create);

  const all = seats.data?.pages.flatMap((p) => p.data) ?? [];
  const roleName = new Map(roles.data?.pages.flatMap((p) => p.data).map((r) => [r.id, r.name]));
  const titleOf = new Map(all.map((s) => [s.id, s.title]));
  const deOn = new Map(
    (des.data?.pages.flatMap((p) => p.data) ?? []).flatMap((d) =>
      d.seat ? [[d.seat.id, d.display_name] as const] : [],
    ),
  );
  const term = search.trim().toLowerCase();
  // WAVE12-FIX: every seat, not only the first page (a new seat sits on the last one, so the list
  // and its search missed it past 50 seats).
  useEveryPage(seats);
  const shown = all.filter(
    (s) =>
      (filter === "all" || (filter === "closed") === (s.status === "closed")) &&
      (!term || s.title.toLowerCase().includes(term)),
  );

  return (
    <SetupPage
      crumbs={[setupCrumb(company), "Structure"]}
      icon={ArmchairIcon}
      title="Seats"
      width="full"
      {...(seats.data ? { count: all.length } : {})}
      actions={
        <Button size="sm" onClick={() => setCreating(true)} disabled={!roles.data}>
          <PlusIcon data-icon="inline-start" />
          New seat
        </Button>
      }
      toolbar={
        <Toolbar bleed>
          <Segmented
            aria-label="Seat status"
            size="sm"
            value={filter}
            onValueChange={setFilter}
            options={[
              { value: "active", label: "Active" },
              { value: "closed", label: "Closed" },
              { value: "all", label: "All" },
            ]}
          />
          <SearchField
            aria-label="Search seats"
            placeholder="Search seats…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span
            className="ml-auto text-xs text-muted-foreground tabular-nums"
            data-slot="seat-count"
          >
            {seats.data ? `${shown.length} of ${all.length}${seats.hasNextPage ? "+" : ""}` : ""}
          </span>
        </Toolbar>
      }
    >
      <div className="flex min-w-0 flex-col px-6 py-4 max-md:px-4">
        {seats.isPending ? (
          <Loading label="Loading seats" rows={5} />
        ) : seats.isError ? (
          <ErrorNotice what="Seats" error={seats.error} />
        ) : !all.length ? (
          <EmptyState
            variant="card"
            icon={ArmchairIcon}
            title="No seats yet"
            actions={<Button onClick={() => setCreating(true)}>New seat</Button>}
          />
        ) : (
          <>
            <Table data-slot="seats" className={DENSE_TABLE}>
              <TableCaption className="sr-only">Seats of {company.name}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Seat</TableHead>
                  <TableHead scope="col" className="max-md:hidden">
                    Role
                  </TableHead>
                  <TableHead scope="col" className="max-md:hidden">
                    Reports to
                  </TableHead>
                  <TableHead scope="col" className="max-lg:hidden">
                    Digital employee
                  </TableHead>
                  <TableHead scope="col">Status</TableHead>
                  <TableHead scope="col" className="w-20">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.length ? (
                  shown.map((s) => (
                    <TableRow
                      key={s.id}
                      data-seat={s.id}
                      data-state={s.id === seatId ? "selected" : undefined}
                      className="group/row"
                    >
                      <TableHead scope="row">
                        <button
                          type="button"
                          className="text-left font-medium text-foreground underline-offset-4 hover:underline"
                          onClick={() => onSeat(s.id)}
                        >
                          {s.title}
                        </button>
                      </TableHead>
                      <TableCell className="max-md:hidden">
                        {roleName.get(s.role_id) ?? "—"}
                      </TableCell>
                      <TableCell className="max-md:hidden">
                        {s.supervisor_seat_id ? (titleOf.get(s.supervisor_seat_id) ?? "—") : "—"}
                      </TableCell>
                      <TableCell className="max-lg:hidden">{deOn.get(s.id) ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={SEAT_STATUS_TONE[s.status]}>
                          {SEAT_STATUS_LABELS[s.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-1 text-right">
                        <RowActions>
                          {/* UX-A 1.7: the seat page is the seat's one page. */}
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <Link
                                  to="/c/$slug/kpis/chart/seat/$id"
                                  params={{ slug: company.slug, id: s.id }}
                                  search={keepFocus}
                                  aria-label={`Open the seat page of ${s.title}`}
                                  className={cn(
                                    buttonVariants({ variant: "ghost", size: "icon-sm" }),
                                    "text-muted-foreground hover:text-foreground",
                                  )}
                                />
                              }
                            >
                              <ArrowUpRightIcon aria-hidden="true" />
                            </TooltipTrigger>
                            <TooltipContent>Open the seat page</TooltipContent>
                          </Tooltip>
                          <RowAction
                            icon={PencilIcon}
                            label={`Edit ${s.title}`}
                            onClick={() => onSeat(s.id)}
                          />
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">
                      No seat matches.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <MoreRows what="seats" q={seats} />
          </>
        )}
      </div>

      {creating && roles.data ? (
        <NewSeatDialog
          api={api}
          co={company.id}
          roles={roles.data.pages.flatMap((p) => p.data)}
          seats={all.filter((s) => s.status !== "closed")}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onSeat(id);
          }}
        />
      ) : null}
      {seatId ? (
        <SeatDrawer
          api={api}
          company={company}
          seatId={seatId}
          seats={all}
          roles={roles.data?.pages.flatMap((p) => p.data) ?? []}
          onClose={() => onSeat(undefined)}
        />
      ) : null}
    </SetupPage>
  );
}

/**
 * "New seat" (Setup › Seats, and the org chart's "Add a report", Chart UX pass 2026-10-21, which
 * presets the supervisor). The caller refreshes what it reads; the seats list is refreshed here.
 * GUIDED-FLOW: an optional "Held by" (Person or DE) follows creation with Send seat setup, or with
 * hiring the DE and opening its guided onboarding; `onCreated` runs once that closes.
 */
export function NewSeatDialog({
  api,
  co,
  roles,
  seats,
  supervisorId = null,
  onClose,
  onCreated,
}: {
  api: Api;
  co: string;
  roles: { id: string; name: string }[];
  seats: Pick<Seat, "id" | "title">[];
  /** The seat the new one reports to, when the caller knows it. */
  supervisorId?: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { me, company } = useSession();
  const [title, setTitle] = useState("");
  const [roleId, setRoleId] = useState(roles[0]?.id ?? "");
  const [supervisor, setSupervisor] = useState(supervisorId ?? "");
  // GUIDED-FLOW: who holds the seat (optional): a person gets "Send seat setup" offered after
  // the seat exists; a DE is hired from a template and its guided onboarding opens.
  const [holder, setHolder] = useState<HolderKind | null>(null);
  const [deChoice, setDeChoice] = useState("");
  const [deName, setDeName] = useState("");
  const [made, setMade] = useState<{ id: string; title: string } | null>(null);
  const [after, setAfter] = useState<{ kind: "person" } | { kind: "de"; runId: string } | null>(
    null,
  );
  // A DE hired by an earlier try (its flow send failed): the retry only sends the flow.
  const hired = useRef<string | null>(null);
  const [deKeys] = useState(() => ({ hire: newIdempotencyKey(), send: newIdempotencyKey() }));
  const choices = useDeChoices(api, co, company, holder === "de");
  const choice = choices.find((c) => c.id === deChoice) ?? null;
  const myMembership = me.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  const create = useMutation({
    mutationFn: async () => {
      // A seat made by an earlier try (whose DE step failed) is not made twice.
      const seat =
        made ??
        (await api.client.seats.create({
          params: { co },
          body: {
            title: title.trim(),
            role_id: roleId,
            supervisor_seat_id: supervisor || null,
          },
        }));
      setMade({ id: seat.id, title: seat.title });
      void qc.invalidateQueries({ queryKey: keys.seats(co) });
      const runId =
        holder === "de" && choice
          ? await hireDeWithOnboarding(api, co, {
              seatId: seat.id,
              choice,
              name: deName,
              me: myMembership,
              hired: hired.current,
              onHired: (id) => {
                hired.current = id;
              },
              keys: deKeys,
            })
          : null;
      return { seat, runId };
    },
    onSuccess: ({ seat, runId }) => {
      if (holder === "person" && company) setAfter({ kind: "person" });
      else if (holder === "de" && runId && company) setAfter({ kind: "de", runId });
      else onCreated(seat.id);
    },
  });
  if (after && made && company)
    return after.kind === "person" ? (
      <SendSeatSetupDialog
        api={api}
        company={company}
        seat={made}
        onClose={() => onCreated(made.id)}
      />
    ) : (
      <FlowModal
        api={api}
        company={company}
        runId={after.runId}
        onClose={() => onCreated(made.id)}
      />
    );
  const deReady = holder !== "de" || (!!choice && !!deName.trim() && !!supervisor);
  return (
    <QuickAddDialog
      title="New seat"
      info="A position in the company. Staff it once it exists; a digital employee's seat needs a supervisor seat held by a person."
      submitLabel="Create seat"
      canSubmit={!!title.trim() && !!roleId && deReady}
      pending={create.isPending}
      onSubmit={() => create.mutate()}
      onClose={onClose}
      slot="new-seat-dialog"
      error={<FormError what="Creating the seat" error={create.error} />}
    >
      <Field>
        <FieldLabel htmlFor="new-seat-name">Title</FieldLabel>
        <Input
          id="new-seat-name"
          value={title}
          required
          maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field>
          <FieldLabel htmlFor="new-seat-role">Role</FieldLabel>
          <NativeSelect
            id="new-seat-role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            {roles.map((r) => (
              <NativeSelectOption key={r.id} value={r.id}>
                {r.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {!roles.length ? (
            <FieldDescription>Create a role under People & roles first.</FieldDescription>
          ) : null}
        </Field>
        <Field>
          <FieldLabel htmlFor="new-seat-supervisor">Reports to</FieldLabel>
          <NativeSelect
            id="new-seat-supervisor"
            value={supervisor}
            onChange={(e) => setSupervisor(e.target.value)}
          >
            <NativeSelectOption value="">No one (top of the chart)</NativeSelectOption>
            {seats.map((s) => (
              <NativeSelectOption key={s.id} value={s.id}>
                {s.title}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      </div>
      <SeatHolderChoice
        holder={holder}
        onHolder={setHolder}
        choices={choices}
        choice={deChoice}
        onChoice={setDeChoice}
        name={deName}
        onName={setDeName}
      />
    </QuickAddDialog>
  );
}
